/**
 * Retorno da autorização da Nuvemshop (app de parceiro / OAuth 2).
 *
 * A Nuvemshop redireciona o lojista para cá com ?code=&state=. O `code` vale
 * apenas 5 minutos, então trocamos por token no backend imediatamente. O token
 * não expira e vem acompanhado do id da loja — por isso o cliente não precisa
 * informar nada manualmente.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getBackendUrl } from '../utils/api';
import {
  limparInstalacaoPendente,
  marcarInstalacaoPendente,
  resolverCompanyId,
} from '../utils/nuvemshopInstalacao';

const NuvemshopCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processando');
  const [erro, setErro] = useState(null);
  // StrictMode monta duas vezes em dev; o code é de uso único, então trocamos 1x só.
  const jaTrocou = useRef(false);

  useEffect(() => {
    if (jaTrocou.current) return;
    jaTrocou.current = true;

    const concluir = async () => {
      const code = params.get('code');
      const state = params.get('state');

      if (!code) {
        setStatus('erro');
        setErro('A Nuvemshop não devolveu o código de autorização. Tente conectar novamente.');
        return;
      }

      // Instalação vinda da Loja de Aplicativos: quem clica em "Instalar" pode
      // nem ter conta na IAT ainda. Em vez de barrar com "sessão expirada",
      // levamos para a página de boas-vindas (entrar ou criar conta) — de lá a
      // autorização é refeita com um código novo, porque este expira em 5 min.
      const user = getAuth().currentUser;
      if (!user) {
        marcarInstalacaoPendente();
        navigate('/integracoes/nuvemshop/instalar', { replace: true });
        return;
      }

      try {
        const companyId = await resolverCompanyId(user);
        const token = await user.getIdToken();
        const resp = await fetch(
          `${getBackendUrl()}/api/nuvemshop/oauth/callback?companyId=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ code, state }),
          }
        );
        const data = await resp.json();
        if (!resp.ok) {
          setStatus('erro');
          setErro(data.error || 'Não foi possível concluir a conexão.');
          return;
        }
        limparInstalacaoPendente();
        setStatus('ok');
      } catch (e) {
        setStatus('erro');
        setErro(e.message || 'Erro inesperado ao concluir a conexão.');
      }
    };

    concluir();
  }, [params, navigate]);

  const caixa = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#16161c',
    color: '#e0e0ff',
    padding: '24px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };
  const cartao = {
    maxWidth: '460px',
    width: '100%',
    background: '#1e1e26',
    border: '1px solid #2f2f3a',
    borderRadius: '12px',
    padding: '28px',
    textAlign: 'center',
  };

  return (
    <div style={caixa}>
      <div style={cartao}>
        {status === 'processando' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: '18px' }}>Conectando sua loja…</h2>
            <p style={{ color: '#9A9AA5', fontSize: '14px', margin: 0 }}>
              Estamos concluindo a conexão com a Nuvemshop. Não feche esta página.
            </p>
          </>
        )}
        {status === 'ok' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: '18px', color: '#10b981' }}>
              Loja conectada com sucesso! ✅
            </h2>
            <p style={{ color: '#9A9AA5', fontSize: '14px', margin: '0 0 16px' }}>
              O agente já pode consultar pedidos, produtos e clientes da sua loja.
            </p>
            <div style={{ textAlign: 'left', color: '#9A9AA5', fontSize: '13px', lineHeight: 1.8, marginBottom: 18 }}>
              <div style={{ color: '#E8E8EC', fontWeight: 600, marginBottom: 6 }}>Próximos passos</div>
              1. Conecte seu número de WhatsApp em Integrações.<br />
              2. Configure o agente de IA (o que ele pode responder e quando chamar a equipe).<br />
              3. Faça uma conversa de teste — as respostas já usarão os dados da sua loja.
            </div>
            <button
              type="button"
              onClick={() => navigate('/chat?integracoes=1')}
              style={{
                background: '#F59E0B',
                color: '#1b1b22',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ir para Integrações
            </button>
          </>
        )}
        {status === 'erro' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: '18px', color: '#ef4444' }}>
              Não foi possível conectar
            </h2>
            <p style={{ color: '#9A9AA5', fontSize: '14px', margin: '0 0 18px' }}>{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/chat?integracoes=1')}
              style={{
                background: '#F59E0B',
                color: '#1b1b22',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Voltar para Integrações
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default NuvemshopCallback;
